// Hook: useTauriFileDrop
//
// Bridges Tauri's native OS file drag-drop into the chat upload
// pipeline. Desktop webviews intercept OS file drags before the DOM
// sees them (`dragDropEnabled: true`), so the HTML5 `DropZone`
// handlers never fire on the desktop build (#7755). This subscribes to
// the native event, reads dropped paths into `File` objects, and hands
// them to the same `onFiles` seam the HTML5 DropZone and file picker
// use — one upload pipeline, two event sources.
//
// No-op on web: `onFileDrop` never attaches a listener off Tauri, so
// the browser keeps its HTML5 `dataTransfer` path unchanged.

import { useEffect, useRef, useState } from "react";

import { onFileDrop, readDroppedFile } from "~/lib/platform";

/**
 * Returns whether OS files are currently hovering the window (so the
 * caller can force the DropZone overlay). Dropped files are read into
 * `File`s and passed to `onFiles`.
 */
export function useTauriFileDrop(onFiles: (files: readonly File[]) => void): boolean {
  const [dragActive, setDragActive] = useState(false);
  // Keep the latest callback in a ref so the effect subscribes once
  // and never tears down / re-adds the webview listener when the
  // callback identity changes (e.g. a room switch rebinds `onFiles`).
  const onFilesRef = useRef(onFiles);
  onFilesRef.current = onFiles;

  useEffect(() => {
    let unlisten: (() => void) | undefined;
    let cancelled = false;
    void onFileDrop({
      onEnter: () => setDragActive(true),
      onLeave: () => setDragActive(false),
      onDrop: (paths) => {
        setDragActive(false);
        void loadDroppedFiles(paths).then((files) => {
          if (!cancelled && files.length > 0) onFilesRef.current(files);
        });
      },
    }).then((fn) => {
      // The component may have unmounted before the async subscribe
      // resolved — unlisten immediately in that case.
      if (cancelled) fn();
      else unlisten = fn;
    });
    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, []);

  return dragActive;
}

async function loadDroppedFiles(paths: readonly string[]): Promise<File[]> {
  const results = await Promise.all(
    paths.map((path) =>
      readDroppedFile(path).catch((err) => {
        console.warn("read dropped file failed", path, err);
        return null;
      }),
    ),
  );
  return results.filter((file): file is File => file !== null);
}
